import { motion } from "framer-motion";
import { ArrowUpRight, Instagram, Mail, MessageCircle, MapPin } from "lucide-react";
import { trackMemberAreaClick } from "@/lib/analytics";
import { memberAreaUrl } from "@/lib/memberUrl";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  },
};

const CONTACTS = [
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: "+62 812-3456-7890",
    href: "https://wa.me/6281234567890",
    testid: "contact-whatsapp",
  },
  {
    icon: Instagram,
    label: "Instagram",
    value: "@davm.official",
    href: "https://instagram.com/davm.official",
    testid: "contact-instagram",
  },
  {
    icon: Mail,
    label: "Email",
    value: "hello@davm.co.id",
    href: "mailto:hello@davm.co.id",
    testid: "contact-email",
  },
  {
    icon: MapPin,
    label: "Alamat",
    value: "Jakarta, Indonesia",
    href: "#",
    testid: "contact-address",
  },
];

export const Footer = () => {
  return (
    <footer
      id="kontak"
      data-testid="footer-section"
      className="relative bg-[#1A1A1A] text-[#FDFBF7] pt-24 md:pt-36 pb-10 overflow-hidden"
    >
      <div className="absolute inset-0 grain-overlay opacity-30 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start"
        >
          <div className="md:col-span-7">
            <p className="text-xs tracking-[0.4em] uppercase text-[#BCA38F] mb-6">
              — Kontak &amp; Layanan
            </p>
            <h2
              data-testid="footer-title"
              className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[1] tracking-tight font-light text-[#FDFBF7]"
            >
              Mulai perjalanan
              <br />
              <em className="italic font-normal text-[#C5A880]">
                cantikmu hari ini.
              </em>
            </h2>
            <p className="mt-8 max-w-lg text-base md:text-lg text-[#FDFBF7]/70 leading-relaxed">
              Bergabunglah dengan ribuan wanita yang merasakan kemewahan
              perawatan DA'VM setiap hari — dan temukan kulit terbaik Anda.
            </p>

            <a
              href={memberAreaUrl()}
              onClick={() => trackMemberAreaClick("footer")}
              data-testid="footer-member-cta"
              className="mt-12 inline-flex items-center gap-3 rounded-full bg-[#FDFBF7] text-[#1A1A1A] px-8 h-14 text-xs tracking-[0.22em] uppercase hover:bg-[#BCA38F] hover:text-[#FDFBF7] transition-all duration-500 group"
            >
              Kunjungi Member Area
              <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>

          <div className="md:col-span-5">
            <ul className="space-y-6">
              {CONTACTS.map((c) => {
                const Icon = c.icon;
                return (
                  <li
                    key={c.label}
                    className="flex items-start gap-5 border-b border-[#FDFBF7]/10 pb-6"
                  >
                    <div className="mt-1 h-10 w-10 rounded-full border border-[#FDFBF7]/20 flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-[#C5A880]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[10px] tracking-[0.32em] uppercase text-[#FDFBF7]/50">
                        {c.label}
                      </p>
                      <a
                        href={c.href}
                        target={c.href.startsWith("http") ? "_blank" : undefined}
                        rel="noopener noreferrer"
                        data-testid={c.testid}
                        className="mt-1 inline-flex items-center gap-2 text-base md:text-lg text-[#FDFBF7] hover:text-[#C5A880] transition-colors duration-500"
                      >
                        {c.value}
                      </a>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </motion.div>

        <div className="mt-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-t border-[#FDFBF7]/10 pt-8">
          <p className="font-serif-display text-2xl tracking-[0.2em] text-[#FDFBF7]">
            DA'VM
          </p>
          <p
            data-testid="footer-copyright"
            className="text-xs tracking-[0.2em] uppercase text-[#FDFBF7]/50"
          >
            © {new Date().getFullYear()} DA&apos;VM. Semua Hak Dilindungi.
          </p>
          <div className="flex items-center gap-6 text-xs tracking-[0.2em] uppercase text-[#FDFBF7]/60">
            <a href="#beranda" className="hover:text-[#FDFBF7] transition-colors">Beranda</a>
            <a href="#produk" className="hover:text-[#FDFBF7] transition-colors">Produk</a>
            <a href="#tentang" className="hover:text-[#FDFBF7] transition-colors">Tentang</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
